import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NbIconLibraries, NbWindowRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { retry } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppAuthService } from './auth/services/app-auth.service';
import { Member } from './shared/models/member/member.model';
import { ExchangeApiService } from './shared/services/exchange-api.service';
import { MemberService } from './shared/services/member.service';
import { MaintenanceService } from './shared/maintenance/maintenance.service';

declare let DD_RUM: any;

@Component({
  selector: 'app-decathlon-exchange',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  loggedIn: Observable<boolean> = this.memberService.loggedIn;
  currentUser: Member = null;
  term = '';
  dialogRef: MatDialogRef<any>;
  reset: Subject<void> = new Subject();
  search = new Subject<void>();
  availableLang: string[] = environment.availableLanguages;
  sideBarVisible = false;
  reportIssuesWindowRef: NbWindowRef;

  /**
   * Check if app is in maintenance mode
   */
  maintenance = false;

  /**
   * Prepare app svg and additional icons,
   * Initiate the tracking by analytics,
   * Set the default values of the translate service
   */
  constructor(
    private translate: TranslateService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private memberService: MemberService,
    private router: Router,
    private dialog: MatDialog,
    private apiService: ExchangeApiService,
    private iconLibraries: NbIconLibraries,
    private appAuth: AppAuthService,
    private maintenanceService: MaintenanceService,
  ) {
    this.registerFontPacks();
    this.matIconRegistry
      .addSvgIconSetInNamespace('sprite',
        this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/sprites/sprite.svg'));

    this.translate.addLangs(this.availableLang);
    this.translate.setDefaultLang('en');
  }

  @HostListener('window:resize')
  onResize() {
    if (this.dialogRef !== undefined) {
      this.dialog.getDialogById('0').updateSize();
      this.term = this.dialogRef.componentInstance.data.term;
    }
  }

  ngOnInit(): void {
    this.appAuth.authenticationRedirectFlow()
      .subscribe(isAuthenticated => {
        if (isAuthenticated) {
          this.setCurrentUser();
        } else {
          // Internal members are automatically connected because we have not opened the website to external members yet
          this.appAuth.decathConnection();
        }
      });

    this.useBrowserLang();
    this.controlPersonalSidebar();
  }

  ngOnDestroy() {
    this.apiService.subject.complete();
  }

  /**
   * Run the initial user fetching and set it in the component
   */
  private setCurrentUser() {
    this.memberService.fetchCurrentUser()
      .pipe(
        retry(2),
      )
      .subscribe(currentUser => {
        this.currentUser = currentUser;

        if (currentUser && currentUser.id) {
          // If maintenance mode is enabled, check if the current user is allowed to access the app
          this.maintenance = this.maintenanceService.shouldShowMaintenance(currentUser);

          const userPermissions = currentUser.roles;

          // Add user info to Datadog RUM
          if (environment.datadog && typeof DD_RUM !== 'undefined') {
            DD_RUM.setUser({
              id: currentUser?.id,
              name: currentUser?.getFullName(),
              email: currentUser?.getEmail(),
              permissions: userPermissions,
            });
          }
        }
      });
  }

  /**
   * Register font packs in nbIcon module
   */
  private registerFontPacks() {
    this.iconLibraries.registerFontPack('fa', {packClass: 'fa', iconClassPrefix: 'fa'});
  }

  /**
   * Retrieves the browser language and uses it as the translation
   * language if it is among the translation files.
   *
   * The translation module is set to provide English otherwise.
   */
  private useBrowserLang() {
    const browserLang = this.translate.getBrowserLang();

    if (browserLang.match(this.availableLang.join('|'))) {
      this.translate.use(browserLang);
    }
  }

  /**
   * Watches the router state to display an additional sidebar
   * in the layout if the user navigate on a personal page
   */
  private controlPersonalSidebar() {
    this.router.events.subscribe(() => {
      const route: string = this.router.url;
      const routeRoot: string = route.split('/')[1];
      this.sideBarVisible = routeRoot === 'me';
    });
  }
}
